import { forwardRef, useMemo } from "react";
import { Chart, useChartTheme, usePrefersReducedMotion, commonStyles, commonTooltip, type ChartHandle, type ChartProps } from "./Chart";

// Stage 6: local replacement for @datasirpi/ui's <Sparkline>. Ported
// letter-for-letter from the vendor's compiled bundle — axis-less line,
// tuned defaults for small inline sizes.
export interface SparklineProps extends Omit<ChartProps, "option" | "tabularData"> {
  data: ReadonlyArray<number>;
  color?: string;
  area?: boolean;
  smooth?: boolean;
  showTooltip?: boolean;
  tableCaption?: string;
}

export const Sparkline = forwardRef<ChartHandle, SparklineProps>(function Sparkline(
  { data, color, area = true, smooth = true, showTooltip = false, tableCaption, height = 40, ...rest },
  ref
) {
  const theme = useChartTheme();
  const reducedMotion = usePrefersReducedMotion();

  const option = useMemo(() => {
    const seriesColor = color ?? theme.palette[0];
    return {
      ...commonStyles(theme, { reducedMotion }),
      grid: { top: 2, right: 2, bottom: 2, left: 2 },
      tooltip: showTooltip ? { trigger: "axis", ...commonTooltip(theme) } : { show: false },
      xAxis: { type: "category", show: false, boundaryGap: false, data: data.map((_, i) => i) },
      yAxis: { type: "value", show: false, scale: true },
      series: [
        {
          type: "line",
          smooth,
          symbol: "none",
          data: [...data],
          itemStyle: { color: seriesColor },
          lineStyle: { color: seriesColor, width: 1.5 },
          areaStyle: area ? { color: seriesColor, opacity: 0.18 } : undefined,
        },
      ],
    };
  }, [data, color, area, smooth, showTooltip, theme, reducedMotion]);

  const tabularData = useMemo(
    () => ({
      ...(tableCaption ? { caption: tableCaption } : {}),
      headers: ["index", "value"],
      rows: data.map((v, i) => [String(i), String(v)]),
    }),
    [data, tableCaption]
  );

  return <Chart ref={ref} option={option} tabularData={tabularData} height={height} {...rest} />;
});
